import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    Draft,
    SerializedError,
} from '@reduxjs/toolkit';
import {
    urlProjectDocumentation,
    urlProjectDocumentationContact,
    urlProjectDocumentationDownload,
    urlProjectDocumentationOpen,
} from '../../Constants';
import { IAction, SliceProps } from '../store';
import { groupBy } from 'lodash';
import { downloadDocumentsStream } from '../../util/FileUtils';
import { apiFetch } from '../../util/ProjectUtils';

const sanitizeString = (value: string): string => {
    return value.replace(/· /g, '\r\n');
};

const organizeProjectDocumentation = (data: IProjectDocumentationItem[]) => {
    const filteredandSortedData = data
        .filter(doc => doc.files[0] !== '')
        .sort((a, b) => a.subject.localeCompare(b.subject));

    return groupBy(filteredandSortedData, ({ subPage }) => subPage);
};
/**
 * Initial slice state value.
 */
const initialState: IProjectDocumentationState = {
    data: null,
    projectDocumentationData: null,
    projectDocumentationContactData: null,
    isDialogOpen: false,
    loading: false,
    isLoadingData: false,
    error: null,
};

/**
 * Collection of the reducers for this slice.
 */
const reducers = {
    openProjectDocumentationDialog(state: IProjectDocumentationState) {
        state.isDialogOpen = true;
    },
    closeProjectDocumentationDialog(state: IProjectDocumentationState) {
        state.isDialogOpen = false;
    },
};

/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    getProjectDocumentation: async (config: {
        replicaId: string;
    }): Promise<IProjectDocumentationItem[]> => {
        try {
            const { replicaId } = config;
            const request: RequestInit = {
                headers: {
                    replicaId,
                },
            };

            const response = await apiFetch(urlProjectDocumentation, request);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    getProjectDocumentationContact: async (config: {
        replicaId: string;
    }): Promise<IProjectDocumentationContactResponse> => {
        try {
            const { replicaId } = config;
            const request: RequestInit = {
                headers: {
                    replicaId,
                },
            };
            const response = await apiFetch(urlProjectDocumentationContact, request);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    downloadProjectDocumentationItem: async (config: {
        replicaId: string;
        docUnId: string;
        fileName: string;
    }): Promise<void> => {
        try {
            const { replicaId, docUnId } = config;
            const request: RequestInit = {
                headers: {
                    replicaId,
                    docUnId,
                },
            };
            const response = await apiFetch(urlProjectDocumentationDownload, request);
            if (response.ok) {
                downloadDocumentsStream(await response.blob(), `${config.fileName}`);
                return Promise.resolve();
            }
            return Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    openProjectDocumentationItem: async (config: {
        replicaId: string;
        universalId: string;
    }): Promise<string> => {
        try {
            const { replicaId, universalId } = config;
            const request: RequestInit = {
                headers: {
                    replicaId,
                    docUnId: universalId,
                },
            };
            const response = await apiFetch(urlProjectDocumentationOpen, request);
            if (response.ok) {
                return Promise.resolve(await response.text());
            }
            return Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IProjectDocumentationState>) => {
        /**
         * Reducers for the 'openProjectDocumentationItem' thunk.
         */

        builder.addCase(
            openProjectDocumentationItem.pending,
            (state: Draft<IProjectDocumentationState>) => {
                state.loading = true;
                state.error = null;
            },
        );
        builder.addCase(
            openProjectDocumentationItem.fulfilled,
            (state: Draft<IProjectDocumentationState>, action: IAction<string>) => {
                state.loading = false;
                state.error = null;
                state.data = action.payload;
            },
        );
        builder.addCase(
            openProjectDocumentationItem.rejected,
            (state: Draft<IProjectDocumentationState>, action: IAction<SerializedError>) => {
                state.loading = false;
                state.error = action.error;
                state.data = null;
            },
        );
        /**
         * Reducers for the 'getProjectDocumentation' thunk.
         */

        builder.addCase(
            getProjectDocumentation.pending,
            (state: Draft<IProjectDocumentationState>) => {
                state.error = null;
                state.isLoadingData = true;
            },
        );
        builder.addCase(
            getProjectDocumentation.fulfilled,
            (
                state: Draft<IProjectDocumentationState>,
                action: IAction<IProjectDocumentationItem[]>,
            ) => {
                state.error = null;
                state.projectDocumentationData = organizeProjectDocumentation(action.payload);

                state.isLoadingData = false;
            },
        );
        builder.addCase(
            getProjectDocumentation.rejected,
            (state: Draft<IProjectDocumentationState>, action: IAction<SerializedError>) => {
                state.isLoadingData = false;
                state.error = action.error;
                state.data = null;
            },
        );
        /**
         * Reducers for the 'getProjectDocumentationContact' thunk.
         */

        builder.addCase(
            getProjectDocumentationContact.pending,
            (state: Draft<IProjectDocumentationState>) => {
                state.error = null;
                state.isLoadingData = true;
            },
        );
        builder.addCase(
            getProjectDocumentationContact.fulfilled,
            (
                state: Draft<IProjectDocumentationState>,
                action: IAction<IProjectDocumentationContactResponse>,
            ) => {
                state.isLoadingData = false;
                state.error = null;
                state.projectDocumentationContactData = {
                    ...action.payload.contactInfo,
                    contact: sanitizeString(action.payload.contactInfo.contact),
                };
            },
        );
        builder.addCase(
            getProjectDocumentationContact.rejected,
            (state: Draft<IProjectDocumentationState>, action: IAction<SerializedError>) => {
                state.isLoadingData = false;
                state.error = action.error;
                state.projectDocumentationContactData = null;
            },
        );
        /**
         * Reducers for the 'downloadProjectDocumentationItem' thunk.
         */

        builder.addCase(
            downloadProjectDocumentationItem.pending,
            (state: Draft<IProjectDocumentationState>) => {
                state.loading = true;
                state.error = null;
            },
        );
        builder.addCase(
            downloadProjectDocumentationItem.fulfilled,
            (state: Draft<IProjectDocumentationState>) => {
                state.loading = false;
                state.error = null;
            },
        );
        builder.addCase(
            downloadProjectDocumentationItem.rejected,
            (state: Draft<IProjectDocumentationState>, action: IAction<SerializedError>) => {
                state.loading = false;
                state.error = action.error;
                state.data = null;
            },
        );
    },
};

/**
 * Expose thunk functions as exports.
 */
export const getProjectDocumentation = createAsyncThunk(
    urlProjectDocumentation,
    thunks.getProjectDocumentation,
);

export const getProjectDocumentationContact = createAsyncThunk(
    urlProjectDocumentationContact,
    thunks.getProjectDocumentationContact,
);

export const downloadProjectDocumentationItem = createAsyncThunk(
    urlProjectDocumentationDownload,
    thunks.downloadProjectDocumentationItem,
);

export const openProjectDocumentationItem = createAsyncThunk(
    urlProjectDocumentationOpen,
    thunks.openProjectDocumentationItem,
);

export interface IProjectDocumentationItem {
    docUNID: string;
    idDocIndex: string;
    description: string;
    projectCategory: string;
    subject: string;
    files: string[];
    subPage: string;
    viewable: boolean;
}

export interface IProjectDocumentationContactInfo {
    contact: string;
    supportMail: string;
    supportNumber: string;
    supportSystem?: string;
    supportURL?: string;
}

export interface IProjectDocumentationContactResponse {
    contactInfo: IProjectDocumentationContactInfo;
}

export interface IProjectDocumentationTabs {
    [key: string]: IProjectDocumentationItem[];
}
/**
 * Interface that describes the state object for this redux slice.
 */
export interface IProjectDocumentationState extends SliceProps {
    projectDocumentationData: IProjectDocumentationTabs;
    projectDocumentationContactData: IProjectDocumentationContactInfo;
    data: string;
    isDialogOpen: boolean;
    isLoadingData: boolean;
}

/**
 * Create the redux slice.
 */
export const projectDocumentationSlice = createSlice({
    name: 'projectDocumentationSlice',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

/**
 * Export functions from slice actions.
 */
export const { openProjectDocumentationDialog, closeProjectDocumentationDialog } =
    projectDocumentationSlice.actions;

/**
 * Export slice reducer as default.
 */
export default projectDocumentationSlice.reducer;
